import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { NotificationsBell } from "@/components/NotificationsBell";
import { Camera, LayoutDashboard, ScanLine, Tags, Settings as Cog, Sun, Moon, LogOut, BarChart3, MessageSquareText, Users } from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/dashboard", label: "Orders", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/scan", label: "Scan Station", icon: ScanLine, testid: "nav-scan" },
  { to: "/labels", label: "Labels", icon: Tags, testid: "nav-labels" },
  { to: "/customers", label: "Customers", icon: Users, testid: "nav-customers" },
  { to: "/analytics", label: "Insights", icon: BarChart3, testid: "nav-insights" },
  { to: "/messages", label: "Messages", icon: MessageSquareText, testid: "nav-messages" },
  { to: "/settings", label: "Settings", icon: Cog, testid: "nav-settings" },
];

export function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const toggleTheme = () => {
    const el = document.documentElement;
    el.classList.toggle("dark");
    localStorage.setItem("lf-theme", el.classList.contains("dark") ? "dark" : "light");
  };

  const onLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen relative z-[2]">
      <header className="sticky top-0 z-50 backdrop-blur-md bg-background/80 border-b border-border no-print">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-4">
          <Link to="/dashboard" className="flex items-center gap-2.5 shrink-0" data-testid="brand-home">
            <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
              <Camera className="w-5 h-5 text-primary-foreground" />
            </div>
            <div className="leading-none hidden sm:block">
              <div className="font-heading font-bold text-[15px] tracking-tight">Low Five</div>
              <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Film Lab Ops</div>
            </div>
          </Link>

          <nav className="flex items-center gap-1 ml-2 overflow-x-auto">
            {NAV.map((n) => {
              const active = location.pathname === n.to || location.pathname.startsWith(n.to + "/");
              const Icon = n.icon;
              return (
                <Link key={n.to} to={n.to} data-testid={n.testid}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                  }`}>
                  <Icon className="w-4 h-4" />
                  <span className="hidden md:inline">{n.label}</span>
                </Link>
              );
            })}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <NotificationsBell />
            <button onClick={toggleTheme} data-testid="theme-toggle"
              className="w-9 h-9 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
              <Sun className="w-4 h-4 hidden dark:block" />
              <Moon className="w-4 h-4 block dark:hidden" />
            </button>
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-border" referrerPolicy="no-referrer" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-xs font-semibold">
                {(user?.name || user?.email || "?")[0].toUpperCase()}
              </div>
            )}
            <Button variant="ghost" size="sm" onClick={onLogout} data-testid="logout-btn" className="text-muted-foreground">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">{children}</main>
    </div>
  );
}
